/**
 * UKŁAD SIATKI OKIEN w modalu „Utwórz nowe zgłoszenie" — ile kolumn i co z niepełnym rzędem.
 *
 * [polecenie właściciela 2026-09-20] „w oknie Utworz nowe zgloszenie — zrob mniejsze te miniatury,
 * tak by bylo grid-template-columns: repeat(5,1fr); ale w takiej formie, by dana kategoria
 * dynamicznie wypelniala pole czyli jak wychodzi 2 rzedy a lacznie jest 6, to zrob 2 rzedy po 3,
 * jak jest 4, a w rzedzie 5, to ostatnia kategorie rozciagnij, tak by to bylo fajnie opisane."
 *
 * ── DLACZEGO TO JEST REGUŁA, A NIE KILKA PRZYPADKÓW W `if` ──────────────────────────────────
 * Liczba kafelków w tej siatce jest DYNAMICZNA i zmienia się kilka razy na sekundę: filtr modułu
 * i wyszukiwarka (obie dodane 19.09) przeliczają listę przy każdym znaku. Na produkcji stoi dziś
 * 43 okna (34 IT + 9 HR), a jutro administrator doda czterdzieste czwarte. Ręcznie wypisane
 * przypadki „4, 5, 6" byłyby poprawne w dniu napisania i nieprawdziwe tydzień później — a nikt
 * by tego nie zauważył, bo krzywy rząd nie jest błędem, tylko brzydkim ekranem.
 *
 * ── REGUŁA (dwa kroki, obie liczby wychodzą z arytmetyki, nie z tabeli przypadków) ───────────
 *  1. NAJMNIEJ RZĘDÓW przy suficie kolumn:      rzedy = ceil(n / sufit)
 *  2. ROZŁOŻENIE NA TE RZĘDY PO RÓWNO:          kolumny = min(sufit, ceil(n / rzedy))
 *
 *  n=6, sufit 5 → rzedy = 2 → kolumny = 3      → DWA RZĘDY PO 3, a nie „5 + sierota".
 *  n=7          → rzedy = 2 → kolumny = 4      → 4 + 3 (ostatni rząd dopełniony rozciągnięciem).
 *  n=16         → rzedy = 4 → kolumny = 4      → cztery równe rzędy zamiast 5+5+5+1.
 *  n=43         → rzedy = 9 → kolumny = 5      → siedem rzędów po 5 i dwa po 4 (dopełnione).
 *
 * ⚠ SUFIT 5 JEST SUFITEM, NIE WARTOŚCIĄ STAŁĄ. `repeat(5, 1fr)` z polecenia to prośba o MNIEJSZE
 * miniatury, a nie o pięć kolumn zawsze — zdanie o „2 rzędach po 3" mówi to wprost. Gdyby kolumn
 * było sztywno 5, sześć okien dałoby dokładnie ten rząd z jedną sierotą, którego właściciel
 * zabronił w tym samym zdaniu.
 *
 * ── DOPEŁNIENIE RZĘDÓW SEKCJI ───────────────────────────────────────────────────────────────
 * Rząd sekcji bywa krótszy od liczby kolumn (n=7 przy 4 kolumnach: 4 + 3). Wolne miejsca
 * rozdajemy kafelkom tego rzędu przez `grid-column: span N`, zaczynając OD KOŃCA — „ostatnią
 * kategorię rozciągnij" z polecenia. Suma rozpiętości KAŻDEGO rzędu jest równa liczbie kolumn,
 * więc żaden rząd nie ma dziury po prawej.
 *
 * 🔴 KAFELKI SEKCJI ROZKŁADAMY NA JEJ RZĘDY PO RÓWNO, a nie „pełne rzędy i reszta na końcu".
 * [WERYFIKACJA ADWERSARIALNA 2026-09-20, zmierzone na produkcji] Liczba kolumn wychodzi z SUMY
 * wszystkich sekcji, ale rząd łamie się na każdym nagłówku sekcji — więc sekcja dostaje układ,
 * którego sama by nie wybrała. Produkcja ma dziś 43 okna w ośmiu sekcjach:
 *     Sprzęt komputerowy 6 · Konta i dostępy 6 · Oprogramowanie 5 · Rozwój 5 ·
 *     Sieć 4 · Wsparcie 3 · Bezpieczeństwo 3 · (bez grupy: 2 IT + 9 HR) 11
 * Przy 5 kolumnach i rozdziale „pełne rzędy, reszta na końcu" sekcja SZEŚCIU okien dawała
 * 5 kafelków + JEDEN ROZCIĄGNIĘTY NA CAŁE PIĘĆ KOLUMN, czyli dokładnie tę sierotę (tyle że
 * rozdmuchaną do paska), której właściciel zabronił: „jak wychodzi 2 rzedy a lacznie jest 6,
 * to zrob 2 rzedy po 3". Na produkcji wychodziły z tego TRZY takie paski (6, 6 i 11 okien).
 * Rozkład po równo daje tam 3 + 3, 3 + 3 i 4 + 4 + 3 — tyle samo rzędów, zero pasków.
 * Ten sam rozkład likwiduje przy okazji przypadek 21 kafelków (było: 4×5 + jeden pasek).
 *
 * ── DLACZEGO LICZBA KOLUMN JEST WSPÓLNA, A ROZPIĘTOŚCI LICZY SIĘ PER SEKCJA ──────────────────
 * Siatka jest JEDNYM elementem `grid` (nagłówki grup kategorii zajmują w niej cały rząd), więc
 * `grid-template-columns` jest z definicji wspólne — dwie różne szerokości kafelka w jednym
 * ekranie wyglądałyby jak usterka. Ale rzędy ŁAMIĄ SIĘ na każdym nagłówku sekcji, więc „ostatni
 * rząd" jest pojęciem SEKCJI, nie całej siatki. Dopełnienie liczone globalnie zostawiałoby dziurę
 * w każdej sekcji poza ostatnią.
 *
 * ── CZEGO TU NIE MA (świadomie) ─────────────────────────────────────────────────────────────
 * Progów szerokości ekranu. Zawężenie do 2 i 1 kolumny na wąskim ekranie robi arkusz
 * (`@media` w `MyNewTicketModal.module.css`), bo tylko on wie, ile pikseli ma okno; ten moduł
 * odpowiada na pytanie „ile kolumn dla TYLU kafelków", a nie „ile kolumn na TYM ekranie".
 * Arkusz zeruje tam także rozpiętości — `span 5` w siatce dwukolumnowej tworzy kolumny domyślne
 * i rozwala układ.
 */

/** Sufit kolumn z polecenia właściciela („zrob mniejsze te miniatury […] repeat(5,1fr)"). */
export const SUFIT_KOLUMN = 5;

/** Liczba całkowita nieujemna albo 0 — dane do układu idą z długości list, ale nie ufamy im na słowo. */
const naturalna = (w) => {
    const n = Math.floor(Number(w));
    return Number.isFinite(n) && n > 0 ? n : 0;
};

/**
 * Ile kolumn ma mieć siatka przy `ileOkien` kafelkach.
 *
 * @param {number} ileOkien  liczba kafelków WIDOCZNYCH w tej chwili (po filtrze i wyszukiwarce)
 * @param {number} sufit     maksymalna liczba kolumn (domyślnie {@link SUFIT_KOLUMN})
 * @returns {number} liczba kolumn, zawsze ≥ 1 i ≤ `sufit`
 */
export function liczbaKolumnSiatki(ileOkien, sufit = SUFIT_KOLUMN) {
    const n = naturalna(ileOkien);
    const cap = Math.max(1, naturalna(sufit) || SUFIT_KOLUMN);
    /* Zero kafelków to pusty stan (biała karta W-1), który i tak zajmuje cały rząd — jedna kolumna
       jest tu wartością obojętną, a nie decyzją o wyglądzie. Nie zwracamy 0: `repeat(0, 1fr)`
       jest nieprawidłową wartością CSS i zabiłoby całą siatkę, gdyby kiedyś trafiło do arkusza. */
    if (n === 0) return 1;
    const rzedy = Math.ceil(n / cap);
    return Math.min(cap, Math.ceil(n / rzedy));
}

/**
 * Ile kafelków stoi w kolejnych rzędach sekcji — rozkład PO RÓWNO.
 *
 * Liczba rzędów jest ta sama, co przy rozdziale „pełne rzędy i reszta" (`ceil(m / k)`), ale
 * kafelki rozkładają się na nie równo, więc żaden rząd nie zostaje z jednym kafelkiem
 * rozciągniętym na całą szerokość. Nadmiar (`m % rzedy`) dostają rzędy od GÓRY — cięższy
 * rząd na górze czyta się naturalniej niż lżejszy.
 *
 * @param {number} ileWSekcji ile kafelków ma ta sekcja
 * @param {number} kolumny    liczba kolumn siatki
 * @returns {number[]} liczności kolejnych rzędów; suma == `ileWSekcji`, długość == `ceil(m / k)`
 */
export function rzedySekcji(ileWSekcji, kolumny) {
    const m = naturalna(ileWSekcji);
    const k = Math.max(1, naturalna(kolumny));
    if (m === 0) return [];
    const rzedy = Math.ceil(m / k);
    const podstawa = Math.floor(m / rzedy);
    const zNadmiarem = m % rzedy;
    return Array.from({ length: rzedy }, (_, r) => podstawa + (r < zNadmiarem ? 1 : 0));
}

/**
 * Rozpiętości (`grid-column: span N`) kafelków JEDNEJ sekcji.
 *
 * Kafelki sekcji rozkładają się na jej rzędy PO RÓWNO ({@link rzedySekcji}), a w obrębie rzędu
 * dzielą między siebie całą szerokość — nadwyżka idzie od końca, więc „rozciągnięty" jest
 * ostatni kafelek rzędu („ostatnia kategorie rozciagnij", cytat właściciela).
 *
 * @param {number} ileWSekcji  ile kafelków ma ta sekcja
 * @param {number} kolumny     liczba kolumn siatki (z {@link liczbaKolumnSiatki})
 * @returns {number[]} tablica długości `ileWSekcji`; suma KAŻDEGO rzędu == `kolumny`
 */
export function rozpietosciSekcji(ileWSekcji, kolumny) {
    const k = Math.max(1, naturalna(kolumny));
    const rozpietosci = [];
    rzedySekcji(ileWSekcji, k).forEach((wRzedzie) => {
        const podstawa = Math.floor(k / wRzedzie);
        const nadwyzka = k % wRzedzie;              // tyle kafelków rzędu dostaje o kolumnę więcej
        for (let i = 0; i < wRzedzie; i += 1) {
            // Nadwyżkę dostają kafelki OD KOŃCA — „ostatnia kategorie rozciagnij" (cytat właściciela).
            rozpietosci.push(podstawa + (i >= wRzedzie - nadwyzka ? 1 : 0));
        }
    });
    return rozpietosci;
}

/**
 * Cały układ siatki naraz: wspólna liczba kolumn + rozpiętości per sekcja.
 *
 * @param {number[]} licznosciSekcji  ile kafelków ma każda sekcja, w kolejności renderu
 * @param {number} sufit
 * @returns {{kolumny: number, sekcje: number[][], razem: number}}
 */
export function ukladSiatkiOkien(licznosciSekcji, sufit = SUFIT_KOLUMN) {
    const licznosci = (Array.isArray(licznosciSekcji) ? licznosciSekcji : []).map(naturalna);
    const razem = licznosci.reduce((suma, n) => suma + n, 0);
    const kolumny = liczbaKolumnSiatki(razem, sufit);
    return { kolumny, razem, sekcje: licznosci.map((n) => rozpietosciSekcji(n, kolumny)) };
}

export default ukladSiatkiOkien;
